import { useEffect, useState } from "react"
import { Mail, Send } from "lucide-react"
import { cn } from "@/lib/utils"
import { notifyPendingSend, type PendingSendInfo } from "@/lib/undoSend"

/**
 * "Kunde informieren" Modal — generiert KI-Mail über Termin-Änderung und sendet sie.
 * 1:1-Port von notify-modal-overlay aus admin/calendar.py.
 *
 * Endpoints:
 *  - GET  /api/v1/calendar/contact-email?tenant=&project_match_id=
 *  - POST /admin/calendar/api/generate-notify-email
 *  - POST /admin/calendar/api/send-notify-email
 */
export function NotifyModal({
  open, onClose, tenant, projectId, contactName, title, start, end, description, isChange,
}: {
  open: boolean
  onClose: () => void
  tenant: string
  projectId: string | number
  contactName?: string
  title: string
  start: string
  end: string
  description?: string
  isChange?: boolean
}) {
  const [loading, setLoading] = useState(true)
  const [to, setTo] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [warning, setWarning] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true); setWarning(null); setErr(null)
    setTo(""); setSubject(""); setBody("")

    const run = async () => {
      try {
        const contactRes = await fetch(
          `/api/v1/calendar/contact-email?tenant=${encodeURIComponent(tenant)}&project_match_id=${projectId}`,
          { credentials: "include" },
        ).then((r) => r.json()).catch(() => ({}))
        const contact = contactRes.contact || {}
        const email = contact.email || ""
        // Objektadresse (Baustelle) bevorzugen, Fallback auf Kontakt-Stammadresse.
        // Bug 09.06.2026: Termin-Mail zog die Stammadresse obwohl eine abweichende
        // Objektadresse hinterlegt war.
        const address = contact.project_address || contact.address || ""
        // Anrede + Nachname aus HERO durchreichen — Backend baut deterministisch
        // "Sehr geehrter Herr Mueller" / "Sehr geehrte Frau Mueller" daraus.
        const salutation = contact.salutation || ""
        const lastName = contact.last_name || ""
        // contact_name nur als Fallback wenn Salutation/Nachname leer.
        const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contactName || ""

        const emailRes = await fetch("/api/v1/calendar/notify-email/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            tenant,
            contact_name: fullName,
            salutation,
            last_name: lastName,
            event_title: title,
            event_start: start,
            event_end: end,
            event_description: description || "",
            is_change: !!isChange,
            project_address: address,
          }),
        }).then((r) => r.json()).catch(() => ({}))

        setTo(email)
        setSubject(emailRes.subject || "")
        setBody(emailRes.body || "")
        if (!email) {
          setWarning("Keine E-Mail-Adresse in HERO hinterlegt. Bitte manuell eingeben.")
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [open, tenant, projectId, contactName, title, start, end, description, isChange])

  const send = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      setErr("Bitte alle Felder ausfüllen")
      return
    }
    setErr(null); setSending(true)
    // Pre-Send Sanity-Check (Phase 6.1A, 2026-05-08): Backend prueft `to`
    // gegen HERO-Kontakt-Email des Projekts. Bei Mismatch HTTP 422 mit
    // detail.code=recipient_mismatch — wir zeigen Confirm-Dialog und senden
    // mit force=true erneut wenn User bestaetigt.
    const doSend = async (force: boolean) => {
      return fetch("/api/v1/calendar/notify-email/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tenant, to, subject, body,
          project_match_id: projectId,
          ...(force ? { force: true } : {}),
        }),
      })
    }
    try {
      let r = await doSend(false)
      if (r.status === 422) {
        const j = await r.json().catch(() => ({}))
        const detail = j.detail || {}
        if (detail.code === "recipient_mismatch") {
          const expected = (detail.expected || []).join(", ") || "?"
          const ok = window.confirm(
            "Achtung: Der Empfaenger weicht von der in HERO hinterlegten Kontakt-Email ab.\n\n" +
            `Geht an: ${detail.requested || to}\n` +
            `In HERO hinterlegt: ${expected}\n\n` +
            "Trotzdem senden?",
          )
          if (!ok) {
            setErr("Abgebrochen — Empfaenger pruefen")
            setSending(false)
            return
          }
          r = await doSend(true)
        }
      }
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      // Phase 6.4 Soft-Send Undo-Toast
      try {
        notifyPendingSend(d as PendingSendInfo, `Termin-Info an ${to}`)
      } catch { /* ignore */ }
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSending(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        backdropFilter: "blur(4px)",
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, width: 560, maxWidth: "95vw",
          maxHeight: "90vh", overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid #e5e7eb",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#1a1a2e", display: "flex", alignItems: "center", gap: 8 }}>
            <Mail className="w-4 h-4" />
            {isChange ? "Über Änderung informieren" : "Über Termin informieren"}
          </h3>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#94a3b8", padding: 4 }}
          >×</button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
            <div style={{ marginBottom: 12 }}>E-Mail wird generiert...</div>
            <div className="animate-spin" style={{
              width: 40, height: 40, border: "3px solid #e2e8f0",
              borderTopColor: "#3c4e7e", borderRadius: "50%", margin: "0 auto",
            }} />
          </div>
        ) : (
          <>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
              {warning && (
                <div style={{
                  padding: "8px 12px", background: "#fef3c7", border: "1px solid #fcd34d",
                  borderRadius: 6, fontSize: 12, color: "#92400e",
                }}>{warning}</div>
              )}
              {err && <div className="pp-alert pp-alert-err">{err}</div>}

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>Empfänger</label>
                <input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  readOnly={!warning}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>Betreff</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>Nachricht</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  style={{ width: "100%", fontFamily: "inherit", resize: "vertical" }}
                />
              </div>
            </div>
            <div style={{
              padding: "16px 24px", borderTop: "1px solid #e5e7eb",
              display: "flex", justifyContent: "flex-end", gap: 10,
            }}>
              <button
                onClick={onClose}
                style={{ padding: "8px 18px", border: "1px solid #d1d5db",
                         background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 14 }}
              >Abbrechen</button>
              <button
                onClick={send}
                disabled={sending}
                className={cn(sending && "opacity-50 cursor-not-allowed")}
                style={{ padding: "8px 18px", background: "#1d4ed8", color: "#fff",
                         border: "none", borderRadius: 6, cursor: "pointer",
                         fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Send className="w-3.5 h-3.5" /> {sending ? "Sendet..." : "Senden"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
